import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ContentLayout,
  Header,
  Container,
  SpaceBetween,
  Box,
  Button,
  Tabs,
  ColumnLayout,
  Badge,
  Spinner,
  Alert,
  ExpandableSection,
  FormField,
  Input,
} from "@cloudscape-design/components";
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import * as api from "../api/api";
import { usePagination } from "../hooks/usePagination";
import { useSummaryData } from "../hooks/useSummaryData";
import { useSplitPanel } from "../hooks/SplitPanelContext";
import SummaryTable from "../components/insights/SummaryTable";
import InsightDetailPanel from "../components/insights/InsightDetailPanel";
import SatisfactionTab from "../components/insights/tabs/SatisfactionTab";
import { GenAIQueryPanel } from "../components/GenAIQueryPanel";
import { DEFAULT_PREFERENCES, DEFAULT_SORTING_COLUMN } from "../constants/insightsConstants";

function SummaryDetail() {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const { openSplitPanel, closeSplitPanel, splitPanelOpen } = useSplitPanel();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [activeTabId, setActiveTabId] = useState("recommendations");

  // Estado local para la grilla de llamadas
  const [filteringText, setFilteringText] = useState("");
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [sortingColumn, setSortingColumn] = useState(DEFAULT_SORTING_COLUMN);
  const [sortingDescending, setSortingDescending] = useState(true);
  const [selectedCallItems, setSelectedCallItems] = useState([]);

  // Estado para el panel de insight (Ver insight)
  const [selectedCall, setSelectedCall] = useState(null);
  const [activeInsightTabId, setActiveInsightTabId] = useState("info");
  const [callDetails, setCallDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [insightError, setInsightError] = useState(null);

  // Derivar filtros del detalle del resumen al formato que esperan los hooks
  const callsDateRange = useMemo(() => {
    if (!detail?.dateRange?.from || !detail?.dateRange?.to) return null;
    return { startDate: detail.dateRange.from, endDate: detail.dateRange.to };
  }, [detail]);

  const callsSupervisor = detail?.supervisor || null;

  const callsAgents = useMemo(() => {
    if (!detail?.agent) return [];
    return Array.isArray(detail.agent) ? detail.agent : [detail.agent];
  }, [detail]);

  const callsSelectedJobNames = useMemo(() => {
    if (!detail?.selectedJobNames || !Array.isArray(detail.selectedJobNames)) return null;
    return detail.selectedJobNames.length > 0 ? detail.selectedJobNames : null;
  }, [detail]);

  // Hooks de paginacion y datos reutilizados
  const pagination = usePagination(callsDateRange, callsSupervisor, callsAgents);
  const summaryData = useSummaryData(callsDateRange, callsSupervisor, callsAgents, pagination, preferences, callsSelectedJobNames);

  const pagesCount = pagination.hasMorePages
    ? pagination.currentPageIndex + 1
    : pagination.currentPageIndex;

  useEffect(() => {
    if (!id) {
      setError(t("insights.detail.summaryNotFound", { defaultValue: "Resumen no encontrado" }));
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchDetail = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await api.getSummaryHistoryDetail(id);

        if (cancelled) return;

        if (data && data.error) {
          setError(data.error);
          setDetail(null);
        } else {
          setDetail(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || t("insights.detail.loadError", { defaultValue: "Error al cargar el detalle del resumen" }));
          setDetail(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDetail();
    return () => { cancelled = true; };
  }, [id, t]);

  // Fetch datos de insight para el SplitPanel
  const fetchInsightData = useCallback(async (call) => {
    if (!call) return;

    const s3Key = call.key;
    if (!s3Key) {
      setInsightError(t("insights.errorLoadingInsight", { defaultValue: "Error al cargar la información del insight: No se pudo determinar el key del archivo" }));
      setCallDetails(null);
      return;
    }

    setLoadingDetails(true);
    setInsightError(null);
    setCallDetails(null);

    try {
      const response = await api.get(s3Key);
      let data;
      if (typeof response === 'string') {
        data = JSON.parse(response);
      } else {
        data = response;
      }
      setCallDetails(data);
      setInsightError(null);
    } catch (err) {
      setInsightError(
        t("insights.errorLoadingInsight", { defaultValue: "Error al cargar la información del insight" })
        + `: ${err.message || err.toString()}`
      );
      setCallDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  }, [t]);

  // Handler para "Ver insight" en cada fila
  const handleViewInsight = useCallback((item) => {
    setSelectedCall(item);
    setActiveInsightTabId("info");
    setCallDetails(null);
    setInsightError(null);

    const loadingPanelContent = (
      <InsightDetailPanel
        selectedCall={item}
        callDetails={null}
        loadingDetails={true}
        insightError={null}
        activeTabId="info"
        setActiveTabId={setActiveInsightTabId}
        history={history}
        t={t}
      />
    );
    openSplitPanel(loadingPanelContent, '');
    fetchInsightData(item);
  }, [history, t, openSplitPanel, fetchInsightData]);

  // Actualizar SplitPanel cuando cambian los datos del insight
  useEffect(() => {
    if (selectedCall) {
      const panelContent = (
        <InsightDetailPanel
          selectedCall={selectedCall}
          callDetails={callDetails}
          loadingDetails={loadingDetails}
          insightError={insightError}
          activeTabId={activeInsightTabId}
          setActiveTabId={setActiveInsightTabId}
          history={history}
          t={t}
        />
      );
      openSplitPanel(panelContent, '');
    }
  }, [selectedCall, callDetails, activeInsightTabId, loadingDetails, insightError, openSplitPanel, history, t]);

  // Limpiar selectedCall cuando el panel se cierra
  useEffect(() => {
    if (!splitPanelOpen && selectedCall) {
      const timeoutId = setTimeout(() => {
        setSelectedCall(null);
        setCallDetails(null);
        setInsightError(null);
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [splitPanelOpen, selectedCall]);

  // Handlers de la grilla
  const handlePreferencesChange = ({ detail: prefDetail }) => {
    setPreferences(prefDetail);
    if (prefDetail.pageSize !== preferences.pageSize) {
      pagination.setCurrentPageIndex(1);
    }
  };

  const handleSortingChange = ({ detail: sortDetail }) => {
    setSortingColumn(sortDetail);
    setSortingDescending(sortDetail.sortingDescending);
  };

  const formatDateRange = (dateRange) => {
    if (!dateRange || (!dateRange.from && !dateRange.to)) return "-";
    try {
      const parts = [];
      if (dateRange.from) {
        const d = new Date(dateRange.from);
        parts.push(d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }));
      }
      if (dateRange.to) {
        const d = new Date(dateRange.to);
        parts.push(d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }));
      }
      return parts.join(' - ');
    } catch {
      return "-";
    }
  };

  const formatSupervisor = (val) => {
    if (!val) return t("insights.all", { defaultValue: "Todos" });
    if (Array.isArray(val)) return val.length > 0 ? val.join(", ") : t("insights.all", { defaultValue: "Todos" });
    return val;
  };

  const formatAgent = (val) => {
    if (!val) return t("insights.all", { defaultValue: "Todos" });
    if (Array.isArray(val)) return val.length > 0 ? val.join(", ") : t("insights.all", { defaultValue: "Todos" });
    return val;
  };

  const formatDateTime = (datetime) => {
    if (!datetime) return "-";
    try {
      const date = new Date(datetime.replace(' ', 'T'));
      return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch {
      return datetime;
    }
  };

  if (loading) {
    return (
      <ContentLayout
        header={<Header variant="h1">{t("insights.detail.title", { defaultValue: "Detalle del resumen" })}</Header>}
      >
        <Box textAlign="center" padding={{ vertical: "xxxl" }}>
          <Spinner size="large" />
        </Box>
      </ContentLayout>
    );
  }

  if (error || !detail) {
    return (
      <ContentLayout
        header={<Header variant="h1">{t("insights.detail.title", { defaultValue: "Detalle del resumen" })}</Header>}
      >
        <Alert
          type="error"
          header={t("insights.detail.summaryNotFound", { defaultValue: "Resumen no encontrado" })}
        >
          {error || t("insights.detail.summaryNotFound", { defaultValue: "Resumen no encontrado" })}
        </Alert>
        <Box padding={{ top: "l" }}>
          <Button variant="link" onClick={() => history.push('/history-insights-calls')}>
            {t("insights.detail.backToHistory", { defaultValue: "Volver al historial" })}
          </Button>
        </Box>
      </ContentLayout>
    );
  }

  const recommendationsTab = (
    <SpaceBetween size="l">
      {detail.aspectsToImprove && detail.aspectsToImprove.length > 0 && (
        <Container
          header={
            <Header variant="h3">
              {t("insights.detail.aspectsToImproveTitle", { defaultValue: "Aspectos a mejorar" })}
            </Header>
          }
        >
          <ol style={{ margin: 0, paddingLeft: '20px' }}>
            {detail.aspectsToImprove.map((item, index) => (
              <li key={index} style={{ marginBottom: '12px', lineHeight: '1.6' }}>
                {item}
              </li>
            ))}
          </ol>
        </Container>
      )}

      {detail.recommendations && detail.recommendations.length > 0 && (
        <Container
          header={
            <Header variant="h3">
              {t("insights.detail.recommendationsTitle", { defaultValue: "Recomendaciones" })}
            </Header>
          }
        >
          <ol style={{ margin: 0, paddingLeft: '20px' }}>
            {detail.recommendations.map((item, index) => (
              <li key={index} style={{ marginBottom: '12px', lineHeight: '1.6' }}>
                {item}
              </li>
            ))}
          </ol>
        </Container>
      )}

      {(!detail.aspectsToImprove || detail.aspectsToImprove.length === 0) &&
       (!detail.recommendations || detail.recommendations.length === 0) && (
        <Box textAlign="center" color="text-body-secondary" padding={{ vertical: "l" }}>
          {t("insights.detail.noRecommendations", { defaultValue: "No hay recomendaciones disponibles para este resumen." })}
        </Box>
      )}
    </SpaceBetween>
  );

  const placeholderTab = (
    <Box textAlign="center" color="text-body-secondary" padding={{ vertical: "xxxl" }}>
      <SpaceBetween size="s" alignItems="center">
        <Box variant="h3" color="text-body-secondary">
          {t("insights.detail.comingSoon", { defaultValue: "Próximamente" })}
        </Box>
        <Box variant="p">
          {t("insights.detail.comingSoonDescription", { defaultValue: "Esta sección estará disponible en una futura actualización." })}
        </Box>
      </SpaceBetween>
    </Box>
  );

  const aiQueriesTab = (
    <GenAIQueryPanel queryContext={{ type: "summary", historyId: detail?.id }} id="summaryDetailChatDiv" />
  );

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          actions={
            <Button variant="primary" iconName="download" disabled>
              {t("insights.detail.downloadSummary", { defaultValue: "Descargar resumen" })}
            </Button>
          }
        >
          {t("insights.detail.title", { defaultValue: "Detalle del resumen" })}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* Cabecera con metadatos */}
        <Container>
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">
                {t("insights.detail.name", { defaultValue: "Nombre" })}:
              </Box>
              <Box>{detail.name || t("insights.detail.unnamed", { defaultValue: "Sin nombre" })}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">
                {t("insights.detail.date", { defaultValue: "Fecha" })}:
              </Box>
              <Box>{formatDateTime(detail.generatedAt)}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">
                {t("insights.detail.callsAnalyzed", { defaultValue: "Llamadas analizadas" })}:
              </Box>
              <Box>
                <Badge color="blue">{detail.callsAnalyzed} items</Badge>
              </Box>
            </div>
          </ColumnLayout>
        </Container>

        {/* Filtros aplicados */}
        <ExpandableSection
          variant="container"
          headerText={t("insights.detail.appliedFilters", { defaultValue: "Filtros aplicados" })}
          defaultExpanded
        >
          <ColumnLayout columns={3} variant="text-grid">
            <FormField label={t("insights.detail.dateRangeLabel", { defaultValue: "Rango de fechas" })}>
              <Input value={formatDateRange(detail.dateRange)} readOnly />
            </FormField>
            <FormField label={t("insights.detail.supervisorLabel", { defaultValue: "Supervisores" })}>
              <Input value={formatSupervisor(detail.supervisor)} readOnly />
            </FormField>
            <FormField label={t("insights.detail.agentLabel", { defaultValue: "Agentes" })}>
              <Input value={formatAgent(detail.agent)} readOnly />
            </FormField>
          </ColumnLayout>
        </ExpandableSection>

        {/* Conclusión */}
        <Container
          header={
            <Header variant="h2">
              {t("insights.detail.conclusionTitle", { defaultValue: "Conclusión" })}
            </Header>
          }
        >
          <Box variant="p" lineHeight="body-m">
            {detail.conclusion || t("insights.detail.noConclusion", { defaultValue: "No hay conclusión disponible." })}
          </Box>
        </Container>

        {/* Tabs */}
        <Tabs
          activeTabId={activeTabId}
          onChange={({ detail: tabDetail }) => setActiveTabId(tabDetail.activeTabId)}
          tabs={[
            {
              id: "recommendations",
              label: t("insights.detail.tabRecommendations", { defaultValue: "Recomendaciones" }),
              content: recommendationsTab,
            },
            {
              id: "satisfaction",
              label: t("insights.detail.tabSatisfaction", { defaultValue: "Satisfacción" }),
              content: (
                <SatisfactionTab
                  satisfactionMetrics={detail?.satisfactionMetrics}
                  emotionMetrics={detail?.emotionMetrics}
                  t={t}
                />
              ),
            },
            {
              id: "ai-queries",
              label: t("insights.detail.tabAIQueries", { defaultValue: "Consultas a la IA" }),
              content: aiQueriesTab,
            },
            {
              id: "calls",
              label: t("insights.detail.tabCalls", { defaultValue: "Llamadas" }),
              content: (
                <SummaryTable
                  items={summaryData.tableItems}
                  loading={summaryData.loading}
                  selectedItems={selectedCallItems}
                  onSelectionChange={setSelectedCallItems}
                  filteringText={filteringText}
                  onFilteringTextChange={setFilteringText}
                  currentPageIndex={pagination.currentPageIndex}
                  pagesCount={pagesCount}
                  onPageChange={pagination.setCurrentPageIndex}
                  preferences={preferences}
                  onPreferencesChange={handlePreferencesChange}
                  sortingColumn={sortingColumn}
                  sortingDescending={sortingDescending}
                  onSortingChange={handleSortingChange}
                  dateRange={callsDateRange}
                  onViewInsight={handleViewInsight}
                  t={t}
                />
              ),
            },
          ]}
        />
      </SpaceBetween>
    </ContentLayout>
  );
}

export default SummaryDetail;
